'use client';

import { cn } from '@/lib/utils';

/** Uma fatia da barra, com o preenchimento que o tema dá a ela. */
export interface ISegmentoDaBarra {
  chave: string;
  valor: number;
  /** Papel da cor no tema (`bg-danger`, `bg-warning`): a barra nunca escolhe um tom próprio. */
  classe: string;
}

/** `sm` acompanha uma linha de lista; `md` é a barra que abre um bloco de destaque. */
type TTamanhoDaBarra = 'sm' | 'md';

/** A altura de cada tamanho, no mesmo vocabulário de `size` dos componentes da casa. */
const ALTURA: Readonly<Record<TTamanhoDaBarra, string>> = {
  sm: 'h-1.5',
  md: 'h-2.5',
};

const PCT_CHEIO = 100;

interface BarraSegmentadaProps {
  /** O que vale 100% da barra. Sem total ela fica vazia, em vez de dividir por zero. */
  total: number;
  segmentos: Array<ISegmentoDaBarra>;
  /**
   * O que a barra mostra, para quem usa leitor de tela: ela é o desenho de números que
   * estão escritos ao lado, então não entra na leitura como um gráfico sem nome.
   */
  rotulo: string;
  size?: TTamanhoDaBarra;
  className?: string;
}

/**
 * Uma barra horizontal dividida em fatias de um total. O projeto não tem biblioteca de
 * gráfico, e os dois gráficos desta tela (o risco aberto em vencido e a vencer, e a
 * carteira de cada marca com a parte em risco) são a mesma barra lida de duas formas.
 */
export function BarraSegmentada({ total, segmentos, rotulo, size = 'sm', className }: BarraSegmentadaProps) {
  const visiveis = total > 0 ? segmentos.filter((segmento) => segmento.valor > 0) : [];

  return (
    <div
      className={cn('flex w-full overflow-hidden rounded-full bg-muted', ALTURA[size], className)}
      role="img"
      aria-label={rotulo}
    >
      {visiveis.map((segmento) => (
        <div
          key={segmento.chave}
          className={cn('h-full', segmento.classe)}
          style={{ width: `${Math.min((segmento.valor / total) * PCT_CHEIO, PCT_CHEIO)}%` }}
        />
      ))}
    </div>
  );
}
